import { useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import DeviceUnknownIcon from "@mui/icons-material/DeviceUnknown";
import LaunchIcon from "@mui/icons-material/Launch";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral, primary } from "app/themes/colors";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { BAS } from "constants/patients";
import { formatPhoneNumber } from "utils/common";

export default function TitleBlock() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { activeCallDetails, patient } = useSunoDialer();
  const { extra, callerId } = activeCallDetails || {};

  const displayName = patient?.fullName
    ? patient?.fullName
    : extra?.phone_number
      ? formatPhoneNumber(String(extra?.phone_number))
      : "Phone number is hidden";

  return (
    <Box sx={{ p: 2, pb: 0 }}>
      <Stack
        spacing={1}
        sx={{
          background: "white",
          p: 1.5,
          borderRadius: "16px",
          boxShadow: theme.shadows[3]
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {Boolean(callerId) ? (
              <Avatar
                alt="avatar"
                src={patient?.avatar || ""}
                sx={{
                  cursor: "pointer",
                  width: "70px",
                  height: "70px"
                }}
                slotProps={{ img: { loading: "lazy" } }}
              />
            ) : (
              <Avatar
                alt="avatar"
                sx={{
                  cursor: "pointer",
                  width: "70px",
                  height: "70px",
                  background: grey[300]
                }}
                slotProps={{ img: { loading: "lazy" } }}
              >
                <DeviceUnknownIcon
                  sx={{ fontSize: "34px", color: neutral[700] }}
                />
              </Avatar>
            )}

            <Stack spacing={0.5} width="100%" height="100%" position="relative">
              <Link
                href={callerId ? `/patients/${callerId}` : undefined}
                sx={{
                  color: neutral[900],
                  textDecorationColor: neutral[500],
                  textDecoration: callerId ? undefined : "none",
                  "&:hover": {
                    color: callerId ? misc.purpleText : neutral[900]
                  }
                }}
                onClick={(event) => {
                  event.preventDefault();
                  if (callerId) {
                    navigate(`/patients/${callerId}`);
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: "17px",
                    lineHeight: 1.3
                  }}
                >
                  {displayName}
                  {Boolean(callerId) && (
                    <IconButton
                      size="small"
                      sx={{
                        ml: -0.5,
                        mt: -0.8,
                        transform: "scale(0.6)",
                        opacity: 0.5,
                        "&:hover": {
                          color: misc.purpleText,
                          opacity: 1
                        }
                      }}
                    >
                      <LaunchIcon />
                    </IconButton>
                  )}
                </Typography>
              </Link>

              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: 1.1,
                  color: grey[800]
                }}
              >
                {Boolean(patient?.sex || patient?.age_years)
                  ? [
                      BAS.getFromCode(patient?.sex),
                      patient?.age_years
                        ? `${patient?.age_years} years old`
                        : ""
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : null}
              </Typography>
            </Stack>
          </Stack>
          {!callerId && (
            <Stack
              spacing={1}
              width="80px"
              sx={{
                cursor: "pointer",
                alignItems: "center",

                "&:hover": {
                  color: primary[500]
                }
              }}
              onClick={() => {
                navigate("/patients/new", {
                  state: {
                    phone_number: extra?.phone_number
                      ? formatPhoneNumber(String(extra?.phone_number))
                      : ""
                  }
                });
              }}
            >
              <IconButton
                size="small"
                sx={{
                  border: `solid 1px ${neutral[700]}`,
                  color: neutral[700],
                  width: "30px",
                  height: "30px",
                  "&:hover": {
                    color: primary[500],
                    border: `solid 1px ${primary[500]}`
                  }
                }}
              >
                <AddIcon />
              </IconButton>

              <Typography
                sx={{
                  fontSize: "12px",
                  lineHeight: 1.2,
                  textAlign: "center"
                }}
              >
                New Patient
              </Typography>
            </Stack>
          )}
        </Stack>
        {Boolean(callerId) && (
          <Stack width="100%" direction="row" spacing={1} pt={1}>
            <Link
              href={`/patients/${callerId}/touchpoints/messages`}
              sx={{
                color: neutral[900],
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: misc.purpleText
                }
              }}
              onClick={(event) => {
                event.preventDefault();
                navigate(`/patients/${callerId}/touchpoints/messages`);
              }}
            >
              <Stack direction="row">
                <Typography
                  sx={{
                    fontSize: "15px",
                    lineHeight: 1.3
                  }}
                >
                  Messages
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    ml: -0.75,
                    mt: -1.0,
                    transform: "scale(0.6)",
                    opacity: 0.7,
                    color: neutral[800],
                    "&:hover": {
                      color: misc.purpleText,
                      opacity: 1
                    }
                  }}
                >
                  <LaunchIcon />
                </IconButton>
              </Stack>
            </Link>
            <Link
              href={`/patients/${callerId}/billing`}
              sx={{
                color: neutral[900],
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: misc.purpleText
                }
              }}
              onClick={(event) => {
                event.preventDefault();
                navigate(`/patients/${callerId}/billing`);
              }}
            >
              <Stack direction="row">
                <Typography sx={{ fontSize: "15px", lineHeight: 1.3 }}>
                  Billing
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    ml: -0.75,
                    mt: -1.0,
                    transform: "scale(0.6)",
                    opacity: 0.7,
                    color: neutral[800],
                    "&:hover": {
                      color: misc.purpleText,
                      opacity: 1
                    }
                  }}
                >
                  <LaunchIcon />
                </IconButton>
              </Stack>
            </Link>
            <Link
              href={`/patients/${callerId}/appointments`}
              sx={{
                color: neutral[900],
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: misc.purpleText
                }
              }}
              onClick={(event) => {
                event.preventDefault();
                navigate(`/patients/${callerId}/appointments`);
              }}
            >
              <Stack direction="row">
                <Typography sx={{ fontSize: "15px", lineHeight: 1.3 }}>
                  Appointments
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    ml: -0.75,
                    mt: -1.0,
                    transform: "scale(0.6)",
                    opacity: 0.7,
                    color: neutral[800],
                    "&:hover": {
                      color: misc.purpleText,
                      opacity: 1
                    }
                  }}
                >
                  <LaunchIcon />
                </IconButton>
              </Stack>
            </Link>
            <Link
              href={`/patients/${callerId}/chart-notes`}
              sx={{
                color: neutral[900],
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: misc.purpleText
                }
              }}
              onClick={(event) => {
                event.preventDefault();
                navigate(`/patients/${callerId}/chart-notes`);
              }}
            >
              <Stack direction="row">
                <Typography sx={{ fontSize: "15px", lineHeight: 1.3 }}>
                  Chart&nbsp;Notes
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    ml: -0.75,
                    mt: -1.0,
                    transform: "scale(0.6)",
                    opacity: 0.7,
                    color: neutral[800],
                    "&:hover": {
                      color: misc.purpleText
                    }
                  }}
                >
                  <LaunchIcon />
                </IconButton>
              </Stack>
            </Link>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
